/** Tile highlight duration in the ghost guide sequence. */
export const GHOST_GUIDE_HIGHLIGHT_MS = 600;

/** Gap between consecutive ghost guide highlights. */
export const GHOST_GUIDE_PAUSE_MS = 150;

/** How long all guided cells stay highlighted together after the sequence. */
export const GHOST_GUIDE_HOLD_ALL_MS = 5000;

export function sleep(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

/** Neon ghost overlay for a tile highlighted by the assistant. */
export function ghostGuideTileOverlaySx() {
    return {
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        zIndex: 5,
        boxShadow:
            "inset 0 0 0 3px rgba(0, 255, 200, 0.95), 0 0 18px rgba(0, 255, 200, 0.65), 0 0 32px rgba(56, 189, 248, 0.45)",
        backgroundColor: "rgba(0, 255, 200, 0.14)",
        animation: "slidingGhostGuidePulse 600ms ease-in-out infinite",
        "@keyframes slidingGhostGuidePulse": {
            "0%, 100%": {
                transform: "scale(1)",
                opacity: 0.92,
            },
            "50%": {
                transform: "scale(1.04)",
                opacity: 1,
            },
        },
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            transform: "scale(1.02)",
        },
    };
}

/** Step order badge — large and readable on phone and tablet tiles. */
export function ghostGuideStepBadgeSx(label = "") {
    const text = String(label ?? "");
    const multi = text.includes(",");
    return {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 6,
        minWidth: { xs: multi ? 44 : 36, sm: multi ? 56 : 44 },
        minHeight: { xs: 36, sm: 44 },
        px: multi ? 1 : 0.75,
        borderRadius: "999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(15, 23, 42, 0.82)",
        color: "#fff",
        fontSize: multi
            ? { xs: "0.95rem", sm: "1.15rem" }
            : { xs: "1.25rem", sm: "1.5rem" },
        fontWeight: 800,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
        lineHeight: 1,
        letterSpacing: multi ? "0.01em" : "-0.02em",
        whiteSpace: "nowrap",
        pointerEvents: "none",
        boxShadow: "0 2px 10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
        border: "1.5px solid rgba(255,255,255,0.35)",
    };
}

/**
 * Sequentially flash each tile id without changing puzzle state.
 *
 * @param {number[]} tileIds slice indices to highlight in order
 * @param {{ onHighlight: (sliceIndex: number | null, stepOrder: number | null) => void, onComplete?: () => void }} callbacks
 * @param {{ aborted?: boolean }} [control]
 */
export async function runGhostGuideSequence(tileIds, callbacks, control = {}) {
    const steps = Array.isArray(tileIds) ? tileIds : [];

    for (let i = 0; i < steps.length; i += 1) {
        if (control.aborted) break;
        callbacks.onHighlight(steps[i], i + 1);
        await sleep(GHOST_GUIDE_HIGHLIGHT_MS);
        if (control.aborted) break;
        if (i < steps.length - 1) {
            callbacks.onHighlight(null, null);
            await sleep(GHOST_GUIDE_PAUSE_MS);
        }
    }

    callbacks.onComplete?.();
}
